import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import WebsiteMediaInput from "./WebsiteMediaInput";

export default function WebsiteGalleryMedia() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/gallery-media",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Gallery Photos And Videos");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Gallery Photos And Videos" />
            <TabHeader
                className="px-3 pt-3"
                title="Gallery Photos And Videos"
                highlights={["Add, edit, and delete rows"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: {} })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/gallery-media/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Gallery Photos And Videos Deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    const active = true;
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.id}. {row.title || "Untitled"}</span>
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_active: true, is_published: true, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        requestAPI({
            requestPath: "website/gallery-media",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Gallery Photos And Videos Saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add" : "Edit"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                    <FloatLabel className="mt-5">
                        <InputText id="album_public_id" className="w-full" value={record?.album_public_id ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, album_public_id: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="album_public_id">Album Public Id</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="media_type" className="w-full" value={record?.media_type ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, media_type: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="media_type">Media Type</label>
                    </FloatLabel>
                    <WebsiteMediaInput label="File" type={record?.media_type === "video" ? "video" : "image"} field="url" hint={record?.media_type === "video" ? "The video that plays when someone taps Play Video." : "Required size: 800 ? 800 px (square). A larger photo is scaled to fit this square. The whole photo stays visible."} record={record} setRecord={setRecord} disabled={saving} />
                    <WebsiteMediaInput label="Video cover" field="thumbnail" hint="Required size: 800 ? 800 px (square). A larger picture is scaled to fit before the video plays. The whole picture stays visible." record={record} setRecord={setRecord} disabled={saving} />
                    <FloatLabel className="mt-5">
                        <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="title">Title</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="caption" className="w-full" rows={4} autoResize value={record?.caption || ""} onChange={(event) => setRecord((prev) => ({ ...prev, caption: event.target.value }))} disabled={saving} />
                        <label htmlFor="caption">Caption</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="alt_text" className="w-full" rows={4} autoResize value={record?.alt_text || ""} onChange={(event) => setRecord((prev) => ({ ...prev, alt_text: event.target.value }))} disabled={saving} />
                        <label htmlFor="alt_text">Alt Text</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="duration" className="w-full" value={record?.duration ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, duration: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="duration">Duration</label>
                    </FloatLabel>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="downloadable" checked={!!record?.downloadable} onChange={(event) => setRecord((prev) => ({ ...prev, downloadable: event.checked }))} disabled={saving} />
                        <label htmlFor="downloadable" className={TEXT_SMALL}>Downloadable</label>
                    </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
